import React from "react";
import { Link } from "react-router-dom";
import {
  Zap,
  User,
  MessageCircle,
  UserPlus,
  CheckCircle2,
  TrendingUp,
  ArrowRight,
} from "lucide-react";

// Static content lives outside the component so it isn't rebuilt on every render
const HIGHLIGHTS = ["Lead capture", "Auto nurturing", "Mobile app"];

const ACTIVITY = [
  { icon: UserPlus, title: "New lead captured", text: "Website form · just now", offset: "" },
  { icon: CheckCircle2, title: "Follow-up sent", text: "Automated · 2 min ago", offset: "lg:ml-10" },
  { icon: TrendingUp, title: "Lead converted", text: "Now a loyal customer", offset: "lg:ml-4" },
];

// Flowing wave lines (same motif as the home hero), built once
const WAVES = Array.from({ length: 10 }, (_, i) =>
  `M -30 ${25 + i * 4} C 260 ${85 - i * 3}, 400 ${10 + i * 2}, 720 ${55 + i * 2} S 1160 ${95 - i * 2}, 1470 ${30 + i * 3}`
);

const CallToAction = () => {
  return (
    <section
      id="get-started"
      aria-labelledby="cta-heading"
      className="w-full bg-white px-4 py-10 font-sans sm:px-10 sm:py-12 lg:px-14 lg:py-16 xl:px-20"
    >
      <div className="relative isolate mx-auto w-full max-w-[1500px] overflow-hidden rounded-[1.5rem] sm:rounded-[2rem] bg-gradient-to-br from-[#28194f] via-[#48247f] to-[#702cb0] shadow-[0_30px_80px_-30px_rgba(72,36,127,0.7)] lg:rounded-[3rem]">
        {/* Teal spotlight + soft glows */}
        <div className="pointer-events-none absolute -right-24 top-1/2 -z-10 h-[30rem] w-[30rem] -translate-y-1/2 rounded-full bg-[radial-gradient(circle,rgba(18,223,189,0.28),transparent_65%)]" />
        <div className="pointer-events-none absolute -left-24 -top-24 -z-10 h-80 w-80 rounded-full bg-fuchsia-400/15 blur-3xl" />

        {/* Wave lines along the bottom */}
        <svg
          aria-hidden="true"
          viewBox="0 0 1440 105"
          preserveAspectRatio="none"
          className="pointer-events-none absolute bottom-0 left-0 -z-10 h-[70px] w-full sm:h-[90px]"
        >
          {WAVES.map((d, i) => (
            <path key={i} d={d} fill="none" stroke="#c9b5ff" strokeWidth=".7" strokeOpacity=".35" />
          ))}
        </svg>

        <div className="relative grid items-center gap-10 px-5 py-12 sm:px-12 sm:py-16 lg:grid-cols-[1.15fr_0.85fr] lg:gap-14 lg:px-16 lg:py-20">
          {/* ===== Left: message + actions ===== */}
          <div className="text-center lg:text-left">
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-3 py-1 text-xs font-medium text-[#12dfbd] backdrop-blur-md">
              <Zap className="h-3.5 w-3.5" />
              AI-Powered Leads Management
            </div>

            <h2
              id="cta-heading"
              className="text-balance text-[26px] font-light leading-[1.25] tracking-tight text-white sm:text-[34px] lg:text-[38px] xl:text-[42px]"
            >
              Turn your leads{" "}
              <span className="bg-gradient-to-r from-[#12dfbd] to-[#c9b5ff] bg-clip-text font-normal text-transparent">
                into loyal customers
              </span>
            </h2>

            <p className="mx-auto mt-4 max-w-[560px] text-sm font-light leading-[1.8] text-white/90 sm:text-base lg:mx-0">
              Streamline your lead capture, nurture prospects automatically, and
              close deals faster with LMSBOTS intelligent management platform &
              mobile app.
            </p>

            {/* Buttons */}
            <div className="mt-8 flex flex-col items-center gap-4 sm:flex-row sm:justify-center lg:justify-start">
              <Link
                to="/login"
                className="group inline-flex min-h-[44px] w-full items-center justify-center gap-2 rounded-lg bg-[#12dfbd] px-7 py-3 text-xs font-semibold text-[#102b37] shadow-lg shadow-[#12dfbd]/20 transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#40f0d3] hover:shadow-[#12dfbd]/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white sm:w-auto sm:text-sm"
              >
                <User className="h-4 w-4" />
                Join Us
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>

              <Link
                to="/contact"
                className="inline-flex min-h-[44px] w-full items-center justify-center gap-2 rounded-lg border border-white/20 bg-white/10 px-7 py-3 text-xs font-medium text-white backdrop-blur-md transition-all duration-300 hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white sm:w-auto sm:text-sm"
              >
                <MessageCircle className="h-4 w-4" />
                Contact Us
              </Link>
            </div>

            {/* Quick highlights */}
            <ul className="mt-8 flex flex-wrap items-center justify-center gap-x-5 gap-y-2.5 lg:justify-start">
              {HIGHLIGHTS.map((h) => (
                <li key={h} className="inline-flex items-center gap-2 text-xs font-medium text-white/85 sm:text-sm">
                  <CheckCircle2 className="h-4 w-4 text-[#12dfbd]" strokeWidth={2.5} />
                  {h}
                </li>
              ))}
            </ul>
          </div>

          {/* ===== Right: decorative activity cards (desktop only) ===== */}
          <div aria-hidden="true" className="hidden flex-col gap-4 lg:flex">
            {ACTIVITY.map(({ icon: Icon, title, text, offset }) => (
              <div
                key={title}
                className={`flex items-center gap-4 rounded-2xl border border-white/15 bg-white/10 p-4 shadow-2xl backdrop-blur-md transition-all duration-300 hover:-translate-y-1 hover:border-[#12dfbd]/60 ${offset}`}
              >
                <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-[#12dfbd] text-[#102b37]">
                  <Icon className="h-5 w-5" />
                </span>
                <div>
                  <p className="text-sm font-medium text-white">{title}</p>
                  <p className="text-xs font-light text-white/75">{text}</p>
                </div>
                <span className="ml-auto h-2 w-2 animate-pulse rounded-full bg-[#12dfbd]" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default CallToAction;